import React, { useCallback, useRef, useState } from 'react';
import {
  Animated,
  View,
  ScrollView,
  StyleSheet,
  RefreshControl,
  LayoutAnimation,
  TouchableOpacity,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { REDESIGN_SPACING, REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { useMaintenanceTickets } from '@/hooks/residents/useMaintenanceTickets';
import { usePmsIntegrationEnabled } from '@/features/payments/hooks/usePmsIntegrationEnabled';
import { useAppSelector } from '@/store';
import { IMaintenanceListResponse } from '@/store/api/maintenanceApi';


import { UndergoingMaintenancePage } from '@/components/residents/maintenance/UndergoingMaintenancePage';
import { MobileMaintenanceSkeleton } from './MobileMaintenanceSkeleton';
import {
  PageHeader,
  MaintenanceCTACard,
  MaintenanceHeroCard,
  MaintenanceRequestRow,
  MaintenanceEmptyState,
} from '@/components/residents/redesign';
import { PressableScale } from '@/components/shared/redesign/PressableScale';
import { AnimatedEntrance } from '@/components/shared/redesign/AnimatedEntrance';
import { useReducedMotion } from '@/hooks/shared/useReducedMotion';
import { pickHeroTicket } from '@/utils/residents/maintenance';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { ScreenBackLink } from '@/components/residents/mobile/ScreenBackLink';

type NavigationProp = StackNavigationProp<RootStackParamList>;

const HP = REDESIGN_SPACING.horizontalPadding;

export const MobileMaintenanceScreen: React.FC = () => {
  const { colors, semantic } = useThemeColors();
  const navigation = useNavigation<NavigationProp>();
  const insets = useSafeAreaInsets();
  const pmsEnabled = usePmsIntegrationEnabled();
  const user = useAppSelector((s) => s.auth.user as any);
  const propertyName = user?.property?.marketingName || user?.property?.legalName || '';
  const unitNumber = user?.unitNumber || '';
  const subtitle = unitNumber ? `${propertyName} · Unit ${unitNumber}` : propertyName;

  const { allTickets, openTickets, closedTickets, loading, refresh } = useMaintenanceTickets();
  const [closedExpanded, setClosedExpanded] = useState(false);
  const reduceMotion = useReducedMotion();

  // 0 collapsed → 1 expanded; drives the disclosure chevron's rotation.
  const chevronProgress = useRef(new Animated.Value(0)).current;

  const toggleClosed = useCallback(() => {
    const next = !closedExpanded;
    if (reduceMotion) {
      chevronProgress.setValue(next ? 1 : 0);
    } else {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      Animated.timing(chevronProgress, {
        toValue: next ? 1 : 0,
        duration: 200,
        useNativeDriver: true,
      }).start();
    }
    setClosedExpanded(next);
  }, [closedExpanded, reduceMotion, chevronProgress]);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  const [refreshing, setRefreshing] = useState(false);

  useFocusEffect(
    useCallback(() => {
      refreshRef.current();
    }, [])
  );

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refresh();
    } finally {
      setRefreshing(false);
    }
  }, [refresh]);

  const handleNewRequestPress = useCallback(() => {
    navigation.navigate('CreateMaintenanceTicket');
  }, [navigation]);

  const handleCardPress = useCallback((ticket: IMaintenanceListResponse) => {
    navigation.navigate('MaintenanceDetail', { ticket });
  }, [navigation]);

  if (!pmsEnabled) {
    return <UndergoingMaintenancePage variant="disabled" />;
  }

  if (user?.hasFutureLease) {
    return <UndergoingMaintenancePage variant="future" />;
  }

  const hasTickets = allTickets && allTickets.length > 0;

  const open = openTickets ?? [];
  const closed = closedTickets ?? [];
  const hero = pickHeroTicket(open);
  const otherOpen = hero ? open.filter((t) => t !== hero) : open;

  // Chevron points down when collapsed ("more below"), up when expanded.
  const chevronStyle = {
    transform: [
      {
        rotate: chevronProgress.interpolate({
          inputRange: [0, 1],
          outputRange: ['90deg', '270deg'],
        }),
      },
    ],
  };

  const renderClosedSection = () =>
    closed.length > 0 ? (
      <>
        <TouchableOpacity
          onPress={toggleClosed}
          accessibilityRole="button"
          accessibilityState={{ expanded: closedExpanded }}
          style={styles.closedLink}
        >
          <Text style={[styles.closedLinkText, { color: semantic.interactive.primary }]}>
            {closedExpanded
              ? 'Hide closed requests'
              : `View ${closed.length} closed request${closed.length === 1 ? '' : 's'}`}
          </Text>
          <Animated.View style={chevronStyle}>
            <RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT
              width={12}
              height={12}
              color={semantic.interactive.primary}
            />
          </Animated.View>
        </TouchableOpacity>
        {closedExpanded && (
          <View style={styles.listContainer}>
            {closed.map((ticket, index) => (
              <AnimatedEntrance
                key={ticket.maintenanceRequestId || index}
                index={index}
                disabled={reduceMotion}
              >
                <MaintenanceRequestRow
                  ticket={ticket}
                  dimmed
                  onPress={() => handleCardPress(ticket)}
                />
              </AnimatedEntrance>
            ))}
          </View>
        )}
      </>
    ) : null;

  const renderContent = () => {
    // Skeleton only before the first data arrives. Later refetches (focus,
    // pull-to-refresh) revalidate silently behind the already-rendered list.
    if (loading && !allTickets) {
      return <MobileMaintenanceSkeleton />;
    }

    if (!hasTickets) {
      return (
        <>
          <MaintenanceCTACard onPress={handleNewRequestPress} variant="compact" />
          <MaintenanceEmptyState />
        </>
      );
    }

    return (
      <>
        {hero ? (
          <AnimatedEntrance index={0} disabled={reduceMotion}>
            <MaintenanceHeroCard ticket={hero} onPress={() => handleCardPress(hero)} />
          </AnimatedEntrance>
        ) : (
          <MaintenanceEmptyState
            title="No open requests"
            subtitle="You have no open service requests at the moment"
          />
        )}
        {otherOpen.length > 0 && (
          <>
            <AnimatedEntrance index={1} disabled={reduceMotion} style={styles.sectionHeaderRow}>
              <Text style={[styles.sectionHeader, { color: colors.text.muted }]}>More open</Text>
            </AnimatedEntrance>
            <View style={styles.listContainer}>
              {otherOpen.map((ticket, index) => (
                <AnimatedEntrance
                  key={ticket.maintenanceRequestId || index}
                  index={2 + index}
                  disabled={reduceMotion}
                >
                  <MaintenanceRequestRow
                    ticket={ticket}
                    onPress={() => handleCardPress(ticket)}
                  />
                </AnimatedEntrance>
              ))}
            </View>
          </>
        )}
        {renderClosedSection()}
      </>
    );
  };

  return (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={[
        styles.scrollContent,
        { paddingBottom: 40 + insets.bottom },
      ]}
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={handleRefresh}
          tintColor={colors.text.primary}
          colors={[semantic.interactive.primary]}
          progressBackgroundColor={colors.card.bg}
        />
      }
    >
      <ScreenBackLink style={styles.backLink} />
      <PageHeader
        title="Maintenance"
        subtitle={subtitle}
        rightAction={
          <PressableScale
            onPress={handleNewRequestPress}
            scaleTo={0.9}
            accessibilityRole="button"
            accessibilityLabel="New request"
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            style={({ pressed }) => (pressed ? styles.headerActionPressed : null)}
          >
            <RESIDENTS_SVG_COMPONENTS.CIRCLE_PLUS width={28} height={28} color={semantic.interactive.primary} />
          </PressableScale>
        }
      />
      {renderContent()}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scrollView: { flex: 1 },
  scrollContent: {
    paddingHorizontal: HP,
    gap: 16,
  },
  backLink: {
    marginBottom: -8,
  },
  listContainer: {
    gap: 12,
  },
  sectionHeaderRow: { marginBottom: -4 },
  sectionHeader: { ...REDESIGN_TYPOGRAPHY.captionMedium, textTransform: 'uppercase', letterSpacing: 0.5 },
  closedLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 4,
  },
  closedLinkText: { ...REDESIGN_TYPOGRAPHY.body, fontWeight: '500' },
  headerActionPressed: { opacity: 0.8 },
});

export default MobileMaintenanceScreen;
